import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { format, parseISO } from 'date-fns';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import DateQuickPresets from '../../components/DateQuickPresets';

const MONTHS = [
  { value: 1, label: 'Jan' },
  { value: 2, label: 'Fev' },
  { value: 3, label: 'Mar' },
  { value: 4, label: 'Abr' },
  { value: 5, label: 'Mai' },
  { value: 6, label: 'Jun' },
  { value: 7, label: 'Jul' },
  { value: 8, label: 'Ago' },
  { value: 9, label: 'Set' },
  { value: 10, label: 'Out' },
  { value: 11, label: 'Nov' },
  { value: 12, label: 'Dez' },
];

const ForecastRolling = () => {
  const now = new Date();
  const anoAtual = Number(format(now, 'yyyy'));
  const mesAtual = Number(format(now, 'M'));
  const hojeIso = format(now, 'yyyy-MM-dd');

  const [carregando, setCarregando] = useState(false);
  const [centrosCustos, setCentrosCustos] = useState([]);
  const [contasBancarias, setContasBancarias] = useState([]);
  const [filtros, setFiltros] = useState({
    ano_base: anoAtual,
    mes_base: mesAtual,
    data_base: hojeIso,
    regime: 'competencia',
    incluir_pendentes: false,
    centro_custo_id: 'todos',
    conta_bancaria_id: 'todas',
  });
  const [dados, setDados] = useState({
    resumo: {
      dias_no_mes: 0,
      dias_decorridos: 0,
      realizado: { receitas: 0, despesas: 0, resultado: 0 },
      run_rate_dia: { receitas: 0, despesas: 0, resultado: 0 },
      projecao_fim_mes: { receitas: 0, despesas: 0, resultado: 0 },
    },
    serie: [],
  });

  const formatarValor = (value) =>
    Number(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const carregarFiltros = async () => {
    try {
      const [centrosResponse, contasResponse] = await Promise.all([
        axios.get('/api/centro-custos'),
        axios.get('/api/contas-bancarias'),
      ]);
      setCentrosCustos(Array.isArray(centrosResponse.data) ? centrosResponse.data : []);
      setContasBancarias(Array.isArray(contasResponse.data) ? contasResponse.data : []);
    } catch (error) {
      console.error('Erro ao carregar filtros do forecast:', error);
    }
  };

  const carregarDados = async () => {
    setCarregando(true);
    try {
      const response = await axios.get('/api/forecast-rolling', { params: filtros });
      const payload = response.data || {};
      setDados({
        resumo: payload.resumo || {
          dias_no_mes: 0,
          dias_decorridos: 0,
          realizado: { receitas: 0, despesas: 0, resultado: 0 },
          run_rate_dia: { receitas: 0, despesas: 0, resultado: 0 },
          projecao_fim_mes: { receitas: 0, despesas: 0, resultado: 0 },
        },
        serie: Array.isArray(payload.serie) ? payload.serie : [],
      });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar forecast rolling');
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregarFiltros();
  }, []);

  useEffect(() => {
    carregarDados();
  }, [filtros]);

  const chartData = useMemo(
    () =>
      (dados.serie || []).map((item) => ({
        ...item,
        label: format(parseISO(item.dia), 'dd/MM'),
      })),
    [dados.serie]
  );

  return (
    <div className="animate-fade-in">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Forecast Rolling</h1>
          <p className="text-gray-600">Projeção automática de fim de mês com base no realizado até hoje</p>
        </div>
      </div>

      <div className="card mb-6">
        <div className="card-body">
          <DateQuickPresets
            mode="single"
            date={filtros.data_base}
            onSelect={({ date }) => {
              const baseDate = parseISO(date);
              setFiltros((old) => ({
                ...old,
                data_base: date,
                ano_base: Number(format(baseDate, 'yyyy')),
                mes_base: Number(format(baseDate, 'M')),
              }));
            }}
            className="mb-4"
          />

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-7 gap-3">
            <div>
              <label className="form-label">Ano Base</label>
              <input
                type="number"
                min="2000"
                max="2100"
                className="form-input"
                value={filtros.ano_base}
                onChange={(e) => setFiltros((old) => ({ ...old, ano_base: Number(e.target.value || anoAtual) }))}
              />
            </div>
            <div>
              <label className="form-label">Mês Base</label>
              <select
                className="form-select"
                value={filtros.mes_base}
                onChange={(e) => setFiltros((old) => ({ ...old, mes_base: Number(e.target.value) }))}
              >
                {MONTHS.map((month) => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Data Base</label>
              <input
                type="date"
                className="form-input"
                value={filtros.data_base}
                onChange={(e) => setFiltros((old) => ({ ...old, data_base: e.target.value }))}
              />
            </div>
            <div>
              <label className="form-label">Regime</label>
              <select className="form-select" value={filtros.regime} onChange={(e) => setFiltros((old) => ({ ...old, regime: e.target.value }))}>
                <option value="competencia">Competencia</option>
                <option value="caixa">Caixa</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  checked={filtros.incluir_pendentes}
                  onChange={(e) => setFiltros((old) => ({ ...old, incluir_pendentes: e.target.checked }))}
                />
                <span className="text-sm text-gray-700">Incluir pendentes</span>
              </label>
            </div>
            <div>
              <label className="form-label">Centro de Custo</label>
              <select className="form-select" value={filtros.centro_custo_id} onChange={(e) => setFiltros((old) => ({ ...old, centro_custo_id: e.target.value }))}>
                <option value="todos">Todos</option>
                {centrosCustos.map((centro) => (
                  <option key={centro.id} value={centro.id}>
                    {centro.codigo} - {centro.descricao}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Conta Bancaria</label>
              <select className="form-select" value={filtros.conta_bancaria_id} onChange={(e) => setFiltros((old) => ({ ...old, conta_bancaria_id: e.target.value }))}>
                <option value="todas">Todas</option>
                {contasBancarias.map((conta) => (
                  <option key={conta.id} value={conta.id}>
                    {conta.banco} - {conta.conta}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Dias</p>
            <p className="text-2xl font-bold text-gray-900">{dados.resumo.dias_decorridos} / {dados.resumo.dias_no_mes}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Resultado Realizado</p>
            <p className={`text-2xl font-bold ${Number(dados.resumo.realizado?.resultado || 0) >= 0 ? 'text-success-700' : 'text-danger-700'}`}>
              R$ {formatarValor(dados.resumo.realizado?.resultado)}
            </p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Run Rate/Dia (Resultado)</p>
            <p className={`text-2xl font-bold ${Number(dados.resumo.run_rate_dia?.resultado || 0) >= 0 ? 'text-success-700' : 'text-danger-700'}`}>
              R$ {formatarValor(dados.resumo.run_rate_dia?.resultado)}
            </p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Projeção Fim do Mês</p>
            <p className={`text-2xl font-bold ${Number(dados.resumo.projecao_fim_mes?.resultado || 0) >= 0 ? 'text-success-700' : 'text-danger-700'}`}>
              R$ {formatarValor(dados.resumo.projecao_fim_mes?.resultado)}
            </p>
          </div>
        </div>
      </div>

      <div className="card mb-6">
        <div className="card-header flex justify-between items-center">
          <h3 className="text-lg font-semibold">Resultado Acumulado: Real vs Projetado</h3>
          {carregando && <div className="loader h-5 w-5" />}
        </div>
        <div className="card-body">
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="label" stroke="#6b7280" fontSize={12} />
                <YAxis stroke="#6b7280" fontSize={12} tickFormatter={(v) => `R$ ${(Number(v) / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => [`R$ ${formatarValor(v)}`, 'Valor']} />
                <Legend />
                <Line type="monotone" dataKey="resultado_acumulado_real" name="Realizado" stroke="#16a34a" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="resultado_acumulado_proj" name="Projetado" stroke="#2563eb" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold">Detalhamento Diário</h3>
        </div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Dia</th>
                  <th>Receitas Dia</th>
                  <th>Despesas Dia</th>
                  <th>Resultado Acum. Real</th>
                  <th>Resultado Acum. Projetado</th>
                </tr>
              </thead>
              <tbody>
                {chartData.map((item) => (
                  <tr key={item.dia}>
                    <td>{format(parseISO(item.dia), 'dd/MM/yyyy')}</td>
                    <td className="text-success-700">R$ {formatarValor(item.receitas_dia)}</td>
                    <td className="text-danger-700">R$ {formatarValor(item.despesas_dia)}</td>
                    <td className={item.resultado_acumulado_real == null ? 'text-gray-400' : (Number(item.resultado_acumulado_real) >= 0 ? 'text-success-700' : 'text-danger-700')}>
                      {item.resultado_acumulado_real == null ? '-' : `R$ ${formatarValor(item.resultado_acumulado_real)}`}
                    </td>
                    <td className={Number(item.resultado_acumulado_proj) >= 0 ? 'text-success-700' : 'text-danger-700'}>
                      R$ {formatarValor(item.resultado_acumulado_proj)}
                    </td>
                  </tr>
                ))}
                {chartData.length === 0 && !carregando && (
                  <tr>
                    <td colSpan={5} className="text-center text-gray-500 py-8">Nenhum dado encontrado para os filtros selecionados</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForecastRolling;
